import React from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { createBrowserRouter, createRoutesFromElements, Route, RouterProvider } from 'react-router-dom';
import { SnackbarProvider } from 'notistack';
import { queryClient } from 'lib/react-query';
import theme from './theme';
import { AuthProvider } from 'features/login';
import { Navigate } from 'react-router';
import GlobalErrorBoundary from 'components/GlobalErrorBoundary';
import { GridErrorBoundaryFallback } from 'features/cabinet';
import { CabinetRouter, LoginRouter, ServicesRouter } from 'routers';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { defaultShouldDehydrateQuery } from '@tanstack/query-core';
import { GlobalSettingsProvider } from 'providers';

const persister = createAsyncStoragePersister({
  storage: window.localStorage,
});

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      {LoginRouter}
      <Route
        path="/"
        element={
          <GlobalErrorBoundary FallbackComponent={GridErrorBoundaryFallback}>
            <AuthProvider />
          </GlobalErrorBoundary>
        }
      >
        {ServicesRouter}
        {CabinetRouter}
        <Route path="*" element={<Navigate to="/services" />} />
        <Route path="" element={<Navigate to="/services" />} />
      </Route>
    </>,
  ),
);

const App = () => (
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          dehydrateOptions: {
            shouldDehydrateQuery: (query) =>
              defaultShouldDehydrateQuery(query) &&
              Boolean(query.meta?.persist),
          },
        }}
      >
        <SnackbarProvider
          autoHideDuration={1000}
          maxSnack={1}
          anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
          preventDuplicate
        >
          <GlobalErrorBoundary>
            <GlobalSettingsProvider>
              <RouterProvider router={router} />
            </GlobalSettingsProvider>
          </GlobalErrorBoundary>
        </SnackbarProvider>
      </PersistQueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>
);

export default App;
